import { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import { IndianRupee, Sparkles } from "lucide-react";

export const EarningsCalculator = () => {
  const [units, setUnits] = useState([120]); // units/month
  const [margin, setMargin] = useState([70]); // avg margin per unit

  const monthly = useMemo(() => units[0] * margin[0], [units, margin]);
  const yearly = monthly * 12;

  return (
    <section id="calculator" data-testid="calculator-section" className="section-pad bg-[#064E3B]">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">
            Earnings Calculator
          </div>
          <h2 className="font-display mt-3 text-3xl sm:text-5xl font-black text-white tracking-tight">
            See what you could take home
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#D6D3D1] max-w-md">
            Move the sliders. Numbers update instantly. Most part-time distributors hit
            <b className="text-[#F59E0B]"> 100–200 units</b> in their first three months.
          </p>

          <img
            src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjb2lucyUyMGdyb3dpbmclMjBwbGFudHxlbnwwfHx8fDE3ODE4ODYxMzN8MA&ixlib=rb-4.1.0&q=85"
            alt="Growing earnings"
            className="mt-8 hidden lg:block rounded-2xl w-full max-w-sm h-56 object-cover"
          />
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white rounded-3xl p-7 sm:p-10 border border-[#E7E5E4]">
            <div className="space-y-8">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold uppercase tracking-[0.15em] text-[#57534E]">
                    Units sold per month
                  </label>
                  <span
                    data-testid="calc-units-value"
                    className="font-display font-black text-2xl text-[#064E3B]"
                  >
                    {units[0]}
                  </span>
                </div>
                <Slider
                  data-testid="calc-units-slider"
                  value={units}
                  onValueChange={setUnits}
                  min={20}
                  max={400}
                  step={5}
                  className="mt-4 calc-slider"
                />
                <div className="flex justify-between text-xs text-[#78716C] mt-2">
                  <span>20</span><span>400</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold uppercase tracking-[0.15em] text-[#57534E]">
                    Average margin (₹ / unit)
                  </label>
                  <span
                    data-testid="calc-margin-value"
                    className="font-display font-black text-2xl text-[#064E3B]"
                  >
                    ₹{margin[0]}
                  </span>
                </div>
                <Slider
                  data-testid="calc-margin-slider"
                  value={margin}
                  onValueChange={setMargin}
                  min={40}
                  max={120}
                  step={5}
                  className="mt-4 calc-slider"
                />
                <div className="flex justify-between text-xs text-[#78716C] mt-2">
                  <span>₹40</span><span>₹120</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="rounded-2xl bg-[#FEF3C7] border border-[#F59E0B]/30 p-6">
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#92400E]">
                    Monthly earning
                  </div>
                  <div
                    data-testid="calc-monthly-earning"
                    className="mt-1 flex items-center font-display text-4xl sm:text-5xl font-black text-[#064E3B]"
                  >
                    <IndianRupee className="h-8 w-8" />{monthly.toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="rounded-2xl bg-[#064E3B] p-6">
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#F59E0B]">
                    Yearly earning
                  </div>
                  <div
                    data-testid="calc-yearly-earning"
                    className="mt-1 flex items-center font-display text-4xl sm:text-5xl font-black text-white"
                  >
                    <IndianRupee className="h-8 w-8" />{yearly.toLocaleString("en-IN")}
                  </div>
                </div>
              </div>

              <a
                href="#register"
                data-testid="calc-cta"
                className="pill-btn pill-btn-secondary w-full justify-center"
              >
                <Sparkles className="h-4 w-4" /> Lock in this income — register now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EarningsCalculator;
